import { ContentManager } from '@23degrees/shared/manager/content/content.manager';
import { QuillRendererService } from '@23degrees/shared/services/quill-renderer/quill-renderer.service';
import { ContentSelector2Component } from '@23degrees/statistic/content-selector2';
import {
  ApplicationRef,
  ComponentRef,
  Directive,
  ElementRef,
  EmbeddedViewRef,
  EventEmitter,
  HostBinding,
  Injector,
  Input,
  OnInit,
  Output,
  Renderer2,
  SimpleChanges,
  ViewContainerRef,
  effect,
  inject,
} from '@angular/core';
import { FormatPipe } from '../pipes/format.pipe';

import { BreakPointManager } from '@23degrees/shared/manager/breakpoint/breakpoint.manager';
import { calculateHeightWidth, joinVisual } from '@23degrees/shared/manager/shared/visual.joins';

import { isUri } from 'valid-url';

import { LoadingIndicatorComponent } from '@23degrees/ui/atoms/loading-indicator/loading-indicator.component';

import { Content, Format } from '@23degrees/shared-code/models';
import { Condition } from '../models/conditions';

import { TableTypes } from '@23degrees/shared-code/config/constants';
import { cloneObject, unique } from '@23degrees/shared-code/utilities';
import { EventBus } from '@23degrees/shared/bus/event/event.bus';
import { takeUntil } from 'rxjs/internal/operators/takeUntil';
import { BaseClass } from '../classes/base.class';
import { StyleService } from '../services/style/style.service';
import { debounceNext } from '@23degrees/utilities/debounce.utils';
import { filterCondition, resolveCondition } from '@23degrees/utilities/content.utils';

@Directive({
  selector: '[deltaContent]',
  standalone: true,
  providers: [FormatPipe],
})
export class DeltaContentDirective extends BaseClass implements OnInit {
  private element = inject(ElementRef);
  private renderer = inject(Renderer2);
  private appRef = inject(ApplicationRef);
  private eventBus = inject(EventBus);
  private quillRenderer = inject(QuillRendererService);
  private formatPipe = inject(FormatPipe);
  private contentManager = inject(ContentManager);
  private breakPointManager = inject(BreakPointManager);
  private viewContainerRef = inject(ViewContainerRef);
  private injector = inject(Injector);
  private _sizeFactor: number = 1;

  public onSizeFactorChange = effect(() => {
    const sizeFactor = this.content ? this.breakPointManager.getSizeFactorSignal(this.content.slug)() : 1;
    if (this._sizeFactor !== sizeFactor) {
      if (this.isBasic) {
        this.renderBasicContent();
      } else {
        this.renderContent();
      }
      this._sizeFactor = sizeFactor;
    }
  });

  @HostBinding('class.delta-container') @Input() deltaContentClass = true;

  @Input() deltaContent: any;
  @Input() item: any;
  @Input() preFilterData: any[] = [];
  @Input() renderData: any[] = [];

  @Input() content: Content;
  @Input() tooltipContainer: HTMLElement;
  @Input() isBasic: boolean = false;
  @Input() wyswigDiv: any;
  @Input() ignoreScaling: boolean = false;
  @Input() debugInfo: string;
  @Input() forceUpdate: string;
  @Input() displayScale: number = 1;
  @Input() edit: boolean = false;
  @Input() advancedTooltip?: any;

  @Output() contentSet = new EventEmitter();

  dynamicComponents: ComponentRef<ContentSelector2Component>[] = [];
  loaderComponentInstance: ComponentRef<LoadingIndicatorComponent>;

  public domElem: any = {};

  ngOnInit() {
    this.eventBus.dynamicHeight.pipe(takeUntil(this.componentDestroyed$)).subscribe((event) => {
      const { height, referenceID } = event.context;
      const slug = event.slug;
      if (!Number.isNaN(height) && referenceID && this.domElem[slug]?.style) {
        if (`cs-${referenceID}` === this.domElem[slug]?.id) {
          this.domElem[slug].style.setProperty('height', `${height}px`, 'important');
          this.domElem[slug].style.setProperty('max-height', `${height}px`, 'important');
        }
      }
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    // console.log('DELTA', changes);
    // if (this.edit) {
    //   this.loadEditor();
    // } else {
    //   this.destroyEditor();
    if (this.isBasic) {
      this.renderBasicContent();
    } else {
      this.renderContent();
    }
    // }

    // console.log({ deltaContent: this.deltaContent });
  }

  ngOnDestroy(): void {
    this.destroyHostViews();
    this.destroyEditor();
    //this.destroyLoader();
  }

  destroyEditor() {
    this.viewContainerRef.clear();
  }

  async renderBasicContent() {
    // console.log('[deltaContent]', this.debugInfo, this.deltaContent);
    if (!this.deltaContent) return;

    let inserts: { ops: any[] };

    try {
      inserts = JSON.parse(this.deltaContent);
    } catch (e) {
      this.renderer.setProperty(this.element.nativeElement, 'innerHTML', this.deltaContent);
      return;
    }

    const converter = this.quillRenderer.convertDelta(inserts.ops, this.content ? this.content.slug : null, this.ignoreScaling);
    const slugs = this.getEmbedComponentSlugs(inserts);

    this.renderer.setProperty(this.element.nativeElement, 'innerHTML', converter.convert());

    if (this.isBrowser()) {
      try {
        if (this.element?.nativeElement?.querySelectorAll) {
          const images = this.element.nativeElement.querySelectorAll('img');
          for (const img of images) {
            img.onerror = (event) => {
              this.renderer.setStyle(event.target, 'display', 'none');
              // console.log('IMAGE ERROR');
            };

            img.onload = (event) => {
              // console.log('IMAGE LOADED');
            };
          }
        }
      } catch (e) {
        console.warn('Problems on catching image errors in delta content!', e);
      }
    }

    if (this.isBasic) {
      return;
    }

    // ########################## THIS IS THE ASYNC STUFF - THAT NEEDS DEBOUNCE #############################
    /**
     * Replace all custom Quill Delta tags
     */
    const insertStatements = inserts.ops;
    return { insertStatements, slugs };
  }

  @debounceNext(25)
  async renderContent() {
    const basicContent = await this.renderBasicContent();
    if (!basicContent) {
      return;
    }
    // this.renderer.setStyle(this.element.nativeElement.firstChild, 'transition', 'opacity 0s');
    this.renderer.setStyle(this.element.nativeElement.firstChild, 'opacity', 0);
    //this.makeComponentAChild();

    const { insertStatements, slugs } = basicContent;
    if (Array.isArray(insertStatements)) {
      for (const op of insertStatements) {
        if (op.insert?.inlineValue) {
          /**
           * Replace Quill Delta DOM Element with Data Value
           */
          const nodeId: string = op.insert.inlineValue.nodeId;
          const fieldName: string = op.insert.inlineValue.fieldName;
          const fieldFormat: Format = op.insert.inlineValue.format;
          const notUnique: { enabled: boolean; value: string } = op.insert.inlineValue.notUnique;
          const conditions: Condition[] = op.insert.inlineValue.conditions ? op.insert.inlineValue.conditions : null;
          const conditionsDataSource: string = op.insert.inlineValue.conditionsDataSource;

          const _data = conditionsDataSource === 'renderData' ? this.renderData : this.preFilterData;

          // console.log('inlineValue', fieldName, conditionsDataSource, _data.length, conditions, notUnique);

          let outputValue = '';

          if (conditions) {
            // outputValue = conditionsResult;
            // To execute conditions, _data muss be present
            if (_data && _data.length > 0) {
              const filtered = [];
              conditions.map((condition, idx) => {
                if (!filtered[idx]) {
                  filtered[idx] = [];
                }
                const haystack: any = idx === 0 ? _data : filtered[idx - 1];
                const ordered = resolveCondition(condition, haystack);
                filtered[idx] = ordered[condition.index];
              });

              const lastFiltered = filtered[filtered.length - 1];
              if (lastFiltered && lastFiltered.length > 0) {
                outputValue = lastFiltered[0][fieldName];
              } else {
                outputValue = ''; // No result from conditions
              }

              let conditionData = _data;
              for (const condition of conditions) {
                conditionData = filterCondition(condition, conditionData);
              }

              console.log('lastFiltered vs. conditionData', lastFiltered, conditionData);
            } else {
              console.log('no data is given to execute conditions');
              outputValue = ''; // No data to execute conditions
            }
          } else if (notUnique?.enabled && Array.isArray(this.renderData)) {
            const data = unique(this.renderData.map((row) => row[fieldName]));
            // console.log('notUnique', fieldName, data, notUnique);
            if (data.length === 1) {
              outputValue = data[0];
            } else {
              outputValue = notUnique.value;
            }
          } else {
            outputValue = this.item && this.item[fieldName] !== undefined && this.item[fieldName] !== null ? this.item[fieldName] : null;
          }

          const contentDOM: any = this.element.nativeElement.querySelector(`span[data-node-id="${nodeId}"]`);

          if (contentDOM) {
            const node = this.getChildNode(contentDOM);
            if (outputValue !== null) {
              if (outputValue === '#') {
                // Not Valid Output Value
                node.textContent = '(Multiple Value)';
              } else {
                if (this.content?.typeSpecific.fields?.[fieldName]) {
                  const fieldLocale = this.content?.typeSpecific.fields[fieldName]?.format.locale ?? 'de-DE';
                  const text = this.formatPipe.transform(outputValue, fieldFormat, undefined, fieldLocale);
                  node.textContent = text;
                } else {
                  node.textContent = outputValue;
                }
              }
            } else {
              if (
                this.content?.typeSpecific?.noDataMessages?.inline.length > 0 &&
                this.content?.typeSpecific?.fields &&
                this.content?.typeSpecific?.fields[fieldName] &&
                this.content?.typeSpecific?.fields[fieldName]?.fieldType === 'valueField'
              ) {
                node.textContent = this.content.typeSpecific.noDataMessages.inline;
              } else {
                this.removeNextBreak(contentDOM);
                contentDOM.remove();
              }
            }
          }
        } else if (op.insert?.dynamicLink) {
          const nodeId = op.insert.dynamicLink.nodeId;
          const fieldName = op.insert.dynamicLink.fieldName;
          const title = op.insert.dynamicLink.title;
          const target = op.insert.dynamicLink.target || '_blank';
          const linkUrl = this.item && this.item[fieldName] !== undefined && this.item[fieldName] !== null ? this.item[fieldName] : null;
          const contentDOM: any = this.element.nativeElement.querySelector(`span[data-node-id="${nodeId}"]`);

          if (contentDOM) {
            const node = this.getChildNode(contentDOM);
            if (linkUrl !== null && isUri(linkUrl)) {
              const { italic, underline } = op.attributes ?? {};
              const style = (italic ? 'font-style: italic;' : '') + (underline ? 'text-decoration: underline;' : '');
              node.innerHTML = `<a href="${linkUrl}" target="${target}" style="${style}">${title}</a>`;
            } else {
              this.removeNextBreak(contentDOM);
              contentDOM.remove();
            }
          }
        } else if (op.insert?.dynamicImage) {
          const nodeId = op.insert.dynamicImage.nodeId;
          const fieldName = op.insert.dynamicImage.fieldName;
          const imageUrl = this.item && this.item[fieldName] !== undefined && this.item[fieldName] !== null ? this.item[fieldName] : null;
          const contentDOM: any = this.element.nativeElement.querySelector(`span[data-node-id="${nodeId}"]`);

          if (contentDOM) {
            const node = this.getChildNode(contentDOM);
            if (imageUrl !== null) {
              // CHECKING FOR IMAGE EVENTS HERE
              // const image: HTMLImageElement = this.globalService.document.createElement('img');
              // image.onerror = (event) => {
              //   this.renderer.setStyle(event.target, 'display', 'none');
              // };
              // // image.onload = (event) => {
              // //   this.recalculatedDynamicViews();
              // // };
              // image.src = imageUrl;
              // node.appendChild(image);

              try {
                const img = await this.imagePromise(imageUrl);
                node.appendChild(img);
              } catch (e) {
                console.warn(e);
                this.removeNextBreak(contentDOM);
                contentDOM.remove();
              }

              // node.innerHTML = `<image src="${imageUrl}" />`;
            } else {
              this.removeNextBreak(contentDOM);
              contentDOM.remove();
            }
          }
        } else if (op.insert?.dynamicIframe) {
          const nodeId = op.insert.dynamicIframe.nodeId;
          const fieldName = op.insert.dynamicIframe.fieldName;
          const ratio = op.insert.dynamicIframe.ratio ?? '16 / 9';
          const iFrameUrl: string = this.item && this.item[fieldName] !== undefined && this.item[fieldName] !== null ? this.item[fieldName] : null;
          const contentDOM: any = this.element.nativeElement.querySelector(`span[data-node-id="${nodeId}"]`);

          if (contentDOM) {
            const node = this.getChildNode(contentDOM);
            if (iFrameUrl !== null) {
              try {
                if (iFrameUrl.endsWith('.mp3')) {
                  const audio = document.createElement('audio');
                  const source = document.createElement('source');
                  audio.style.width = '100%';
                  audio.controls = true;
                  source.type = 'audio/mpeg';
                  source.src = iFrameUrl;
                  audio.appendChild(source);
                  node.appendChild(audio);
                } else if (iFrameUrl.endsWith('.mp4')) {
                  const video = document.createElement('video');
                  const source = document.createElement('source');
                  video.style.width = '100%';
                  video.controls = true;
                  source.type = 'video/mp4';
                  source.src = iFrameUrl;
                  video.appendChild(source);
                  node.appendChild(video);
                } else {
                  const iframe = document.createElement('iframe');
                  iframe.src = iFrameUrl;
                  iframe.style.border = '0';
                  iframe.style.width = '100%';
                  iframe.style.aspectRatio = ratio;
                  node.appendChild(iframe);
                }
              } catch (e) {
                console.warn(e);
                this.removeNextBreak(contentDOM);
                contentDOM.remove();
              }

              // node.innerHTML = `<image src="${imageUrl}" />`;
            } else {
              this.removeNextBreak(contentDOM);
              contentDOM.remove();
            }
          }
        }
      }

      for (const op of insertStatements) {
        if (op.insert?.embedVisual) {
          // console.log('[DeltaContentDirective] Insert Visual:');
          /*
        slug:
        id:
        joins :
        title:
        type:
        embedwidth
        sourcetype
        sourcefield
         */
          const nodeId = op.insert.embedVisual.nodeId;
          const widthPercent = op.insert.embedVisual.embedwidth;
          let injectSlug: string;
          let slug: string;
          let baseContent: Content;
          let hasJoins = false;
          // go with defaults!
          let appearanceSettings: { growTillFirstPage: any; naturalHeight?: any; embedWidth?: number; maxHeightEnabled?: boolean; maxHeight?: number };
          if (op.insert.embedVisual.appearancesettings) {
            const settings = JSON.parse(op.insert.embedVisual.appearancesettings);
            if (settings?.embedWidth && settings.maxHeight) {
              appearanceSettings = settings;
            }
          }
          if (!appearanceSettings) {
            // default
            appearanceSettings = {
              embedWidth: 100,
              maxHeightEnabled: true,
              maxHeight: 90,
              growTillFirstPage: false,
            };
          }
          // console.log("APPEARANCE",appearanceSettings)
          if (op.insert.embedVisual.sourcetype === 'data') {
            // - from data == item
            // console.log('[DeltaContentDirective] Adding from: ', op.insert.embedVisual);
            const sourceField = op.insert.embedVisual.sourcefield;
            slug = this.item[sourceField];
            if (slug) {
              // console.log('[DeltaContentDirective] - loading slug: ', slug);
              const loadedContent = await this.contentManager.getContentBySlug(slug, true);
              if (loadedContent?.slug) {
                // console.log("[DeltaCnodeIdontentDirective] - loaded Content: ",loadedContent);
                baseContent = cloneObject(loadedContent);
                baseContent.slug = nodeId;
                baseContent.localStorage.hideScrollbar = true;
                baseContent.localStorage.blockPostMessages = true;
                injectSlug = nodeId;

                // layout hide footer on old visuals
                if (baseContent.typeSpecific.layout) {
                  const { layout } = baseContent.typeSpecific;
                  const activeLayout = layout.layouts[layout.active];

                  // TODO: Nonsense
                  //@ts-ignore
                  if (activeLayout.hideFooterBecauseOldNonsense === true) {
                    const containerFooter = activeLayout.children[2];
                    containerFooter.children = [];
                  }
                }

                this.contentManager.injectAlreadyCachedContentDirectly(baseContent);
              }
            }
          } else if (op.insert.embedVisual.sourcetype === 'slug') {
            // inserting an visual can be either by
            // - from slug with potential joins
            slug = op.insert.embedVisual.slug;
            const loadedContent = await this.contentManager.getContentBySlug(slug, true);
            if (loadedContent) {
              baseContent = cloneObject(loadedContent);
              baseContent.slug = nodeId;
              baseContent.localStorage.hideScrollbar = true;
              baseContent.localStorage.blockPostMessages = true;
              injectSlug = nodeId;

              // layout hide footer on old visuals
              if (baseContent.typeSpecific.layout) {
                const { layout } = baseContent.typeSpecific;
                const activeLayout = layout.layouts[layout.active];

                // TODO: Nonsense
                //@ts-ignore
                if (activeLayout.hideFooterBecauseOldNonsense === true) {
                  const containerFooter = activeLayout.children[2];
                  containerFooter.children = [];
                }
              }

              // console.log("[EmbedVisual Directive]",this.content,op.insert.embedVisual.joins,this.item);
              const joins = JSON.parse(op.insert.embedVisual.joins);
              if (joins.length > 0) {
                if (this.item) {
                  for (const join of joins) {
                    joinVisual(baseContent, join, this.item);
                  }

                  hasJoins = true;
                }
              }

              this.contentManager.injectAlreadyCachedContentDirectly(baseContent);
            }
          } else {
            console.log('[DeltaContentDirective] ERROR no implementation for sourcetype:', op.insert.embedVisual);
          }
          if (injectSlug && baseContent) {
            const isMobile = this.breakPointManager.isMobile(this.content.slug);
            let currentWidth: number;
            let currentHeight: number;

            // get width and height from actual width and height of the tooltip
            // if it is not a
            if (this.content.typeSpecific.advancedTooltip.type === 'classic') {
              currentWidth = this.breakPointManager.getWidth(this.content.slug);
              currentHeight = this.breakPointManager.getHeight(this.content.slug);
              // console.log('[BPM DIV]', this.content.slug, currentWidth, currentHeight);
            } else {
              currentWidth = this.tooltipContainer.offsetWidth;
              currentHeight = this.tooltipContainer.offsetHeight;
              // console.log('[CONTAINER DIV]', currentWidth, currentHeight);
            }

            if (currentWidth <= 0) currentWidth = this.tooltipContainer.offsetWidth;
            if (currentHeight <= 0) currentHeight = this.tooltipContainer.offsetHeight;

            const sizes = calculateHeightWidth(
              baseContent,
              this.content.typeSpecific.advancedTooltip,
              isMobile,
              // 400, 300,
              currentWidth,
              currentHeight,
              appearanceSettings,
            );

            // console.log('[DeltaDirective] - Slugs check: ', slug, injectSlug, slugs);
            let componentRef = this.destroyOtherHostViews(injectSlug, slugs);
            // console.log('[WYSIWIG DIV]', this.wyswigDiv.offsetWidth, this.wyswigDiv.offsetHeight);
            // console.log('[CONTAINER DIV]', this.tooltipContainer.offsetWidth, this.tooltipContainer.offsetHeight);
            // try to get references height and width
            const existingContentHeight = this.wyswigDiv.offsetHeight;

            if (!componentRef) {
              // console.log('[DeltaDirective] - Creating Component');
              // const componentFactory = this.componentFactoryResolver.resolveComponentFactory(ContentSelector2Component);
              // componentRef = componentFactory.create(this.injector);
              // componentRef.setInput('slug', injectSlug ?? slug);
              // componentRef.setInput('options', {
              //   editor: false,
              //   public: true,
              //   hideFooter: true,
              //   displayScale: this.displayScale,
              //   updateAutoViewport: hasJoins,
              // });

              // this.appRef.attachView(componentRef.hostView);

              componentRef = this.viewContainerRef.createComponent(ContentSelector2Component, { injector: this.injector });
              componentRef.setInput('slug', injectSlug ?? slug);
              componentRef.setInput('options', {
                editor: false,
                public: true,
                hideFooter: true,
                displayScale: this.displayScale,
                updateAutoViewport: hasJoins,
              });

              this.dynamicComponents.push(componentRef);
            } else {
              // console.log('[DeltaDirective] - Using Existing Component',injectSlug, slug);
              componentRef.setInput('slug', injectSlug ?? slug);
              componentRef.setInput('options', {
                editor: false,
                public: true,
                hideFooter: true,
                displayScale: this.displayScale,
                updateAutoViewport: hasJoins,
              });
            }

            this.domElem[nodeId] = (componentRef.hostView as EmbeddedViewRef<any>).rootNodes[0] as HTMLElement;
            this.domElem[nodeId].style.width = `${sizes.width}px`;
            // console.log('delta width', sizes);
            // domElem.style.display = 'inline-flex'; was in there to center visuals
            this.domElem[nodeId].style.display = 'flex'; // inline-flex causes weird spacing. dont know if this fixes or breaks it more

            // setting size on grow or not
            if (appearanceSettings.growTillFirstPage) {
              // console.log('[growTillFirstPage] : ', sizes.baseMaxHeight - existingContentHeight);
              let actualHeight = sizes.baseMaxHeight - existingContentHeight;
              // fallback if it is too smal and will never show!;
              if (actualHeight < 60) {
                actualHeight = 60;
              }
              this.domElem[nodeId].style.setProperty('height', `${actualHeight}px`, 'important');
              this.domElem[nodeId].style.setProperty('max-height', `${actualHeight}px`, 'important');
              // domElem.style.setProperty('flex', '1 0 auto');
            } else if (appearanceSettings.naturalHeight) {
              const dynamicHeight = baseContent.typeSpecific.chart.dynamicHeight;
              let height: number = 0;
              if (dynamicHeight || TableTypes.includes(baseContent.type)) {
                height = 100; // Initial height
              } else {
                const { breakPoints } = baseContent.typeSpecific.style;
                const currentWidth = sizes.width;
                const aspectRatio = this.breakPointManager.calcSizeFromBreakPts(breakPoints, currentWidth);
                height = Math.floor(currentWidth / aspectRatio);
              }
              this.domElem[nodeId].style.setProperty('height', `${height}px`, 'important');
              this.domElem[nodeId].style.setProperty('max-height', `${height}px`, 'important');
            } else {
              this.domElem[nodeId].style.setProperty('height', `${sizes.height}px`, 'important');
              this.domElem[nodeId].style.setProperty('max-height', `${sizes.height}px`, 'important');
            }

            // it is in editor mode without being selected as editable
            const contentDOM: any = this.element.nativeElement.querySelector(`[data-node-id="${nodeId}"]`);

            contentDOM.replaceWith(this.domElem[nodeId]);

            // start changes and rerendering when we are ready
            componentRef.setInput('slug', injectSlug ? injectSlug : slug);
            componentRef.setInput('displayScale', this.displayScale);
          }
        } else if (op.insert?.customIframe) {
          const isMobile = this.breakPointManager.isMobile(this.content.slug);
          let currentWidth: number;
          let currentHeight: number;
          const advancedTooltip = this.advancedTooltip || this.content.typeSpecific.advancedTooltip;
          if (advancedTooltip.type === 'classic' || !this.tooltipContainer) {
            currentWidth = this.breakPointManager.getWidth(this.content.slug);
            currentHeight = this.breakPointManager.getHeight(this.content.slug);
          } else {
            currentWidth = this.tooltipContainer.offsetWidth;
            currentHeight = this.tooltipContainer.offsetHeight;
          }
          let appearanceSettings: { embedWidth: number; maxHeightEnabled: boolean; maxHeight: number; growTillFirstPage: boolean };
          if (op.insert.customIframe.appearancesettings) {
            const settings = JSON.parse(op.insert.customIframe.appearancesettings);
            if (settings?.embedWidth && settings.maxHeight) {
              appearanceSettings = settings;
            }
          }

          if (!appearanceSettings) {
            // default
            appearanceSettings = {
              embedWidth: 100,
              maxHeightEnabled: true,
              maxHeight: 100,
              growTillFirstPage: false,
            };
          }

          const sizes = calculateHeightWidth(this.content, advancedTooltip, isMobile, currentWidth, currentHeight, appearanceSettings);
          const frames = (this.element.nativeElement as HTMLElement).querySelectorAll('iframe');

          const tt = getComputedStyle(this.element.nativeElement as HTMLElement);
          const tooltipContentHeight = (this.element.nativeElement as HTMLElement).getBoundingClientRect().height;

          let framesHeight = 0;

          for (const elem of Array.from(frames)) {
            framesHeight += elem.getBoundingClientRect().height;
          }
          const diff = tooltipContentHeight - framesHeight;

          for (const elem of Array.from(frames)) {
            this.renderer.setStyle(elem, 'height', `${sizes.baseMaxHeight - diff}px`);
            // this.renderer.setStyle(elem, 'height', `${sizes.baseMaxHeight - diff - Number.parseFloat(tt.paddingTop) - Number.parseFloat(tt.paddingBottom)}px`);
            this.renderer.setStyle(elem, 'min-width', `${sizes.baseMaxWidth - Number.parseFloat(tt.paddingRight) - Number.parseFloat(tt.paddingLeft)}px`);
            this.renderer.setStyle(elem, 'width', '100%');
          }
        }
      }
    }

    // this.renderer.setStyle(this.element.nativeElement.firstChild, 'transition', 'opacity .25s');
    this.renderer.setStyle(this.element.nativeElement.firstChild, 'opacity', 1);
    //this.destroyLoader();
    this.contentSet.emit();
  }

  /**
   * Get all slugs form the dynamic views which will get rendered to the content
   *
   * @param inserts Delta Inserts
   */
  getEmbedComponentSlugs(inserts): string[] {
    const slugs: string[] = [];
    for (const op of inserts.ops) {
      if (op.insert?.embedVisual) {
        // slugs.push(op.insert.embedVisual.slug);
        // for injected cotnent
        slugs.push(op.insert.embedVisual.nodeId);
      }
    }
    return slugs;
  }

  /**
   * Destroy all dynamic components
   */
  destroyHostViews() {
    if (this.dynamicComponents.length > 0) {
      this.dynamicComponents.forEach((dynamicComponent, idx) => {
        this.appRef.detachView(dynamicComponent.hostView);
        dynamicComponent.destroy();
        this.dynamicComponents.splice(idx, 1);
      });
    }
  }

  /**
   * Check if the current to render (dynamic) component is already appended and get it
   * Destroy all others which wont get into the content
   *
   * @param slug Slug of current visual to render
   * @param slugs Slug array of all ohter embed visuals needed to be rendered
   */
  destroyOtherHostViews(slug: string, slugs: string[]): ComponentRef<ContentSelector2Component> {
    // console.log('[deltaContent] Destroying', slug);
    let component: ComponentRef<ContentSelector2Component>;

    if (this.dynamicComponents.length > 0) {
      this.dynamicComponents.forEach((dynamicComponent, idx) => {
        if (slug === dynamicComponent.instance.slug()) {
          component = dynamicComponent;
        } else if (!slugs.includes(dynamicComponent.instance.slug())) {
          // this.appRef.detachView(dynamicComponent.hostView);
          dynamicComponent.destroy();
          this.dynamicComponents.splice(idx, 1);
        }
      });
    }
    return component;
  }

  /**
   * Get the deepest nested HTML Element of a given node
   *
   * @param node
   */
  getChildNode(node): HTMLElement {
    if (node.hasChildNodes()) {
      return this.getChildNode(node.lastChild);
    }
    return node;
  }

  /**
   * Check if node is surrounded with breaks and remove one of it
   *
   * @param node Node
   */
  removeNextBreak(node: HTMLElement) {
    if (!node) return;
    const prev: any = node.previousElementSibling;
    const next: any = node.nextElementSibling;

    if (next && prev && prev.nodeName === 'BR' && next.nodeName === 'BR') {
      next.remove();
      if (prev?.previousSibling && prev.previousSibling.nodeName === 'BR') {
        prev.remove();
      }
    }
  }

  // removeNextBreaks(node: HTMLElement){
  //   if(!node) return;
  //   const next = node.nextElementSibling as HTMLElement;
  //   if(next && next.nodeName === 'BR'){
  //     console.log('REMOVE IT');
  //     this.removeNextBreak(next);
  //     if(next.nextElementSibling.nodeName === 'BR') next.remove();
  //   }
  //   else{
  //     console.log('next node', next);
  //   }
  // }

  imagePromise(url): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.addEventListener('load', (e) => resolve(img));
      img.addEventListener('error', () => {
        reject(new Error(`Failed to load image's URL: ${url}`));
      });
      img.src = url;
    });
  }

  // private createLoaderComponent() {
  //   const componentFactory = this.componentFactoryResolver.resolveComponentFactory(LoadingIndicatorComponent);
  //   this.loaderComponentInstance = this.viewContainerRef.createComponent(componentFactory);

  //   if (this.content) {
  //     this.loaderComponentInstance.instance.colors = this.content.typeSpecific.style.colors2;
  //     this.loaderComponentInstance.changeDetectorRef.detectChanges();
  //   } else {
  //     this.loaderComponentInstance.instance.ignoreColors = true;
  //   }
  // }

  /*
  private makeComponentAChild() {
    this.destroyLoader();
    this.createLoaderComponent();
    new Observable().pipe(takeUntil(this.loaderDestroy$), take(1), delay(500)).subscribe(() => {
      const loaderComponentElement = this.loaderComponentInstance.location.nativeElement;
      this.element.nativeElement.appendChild(loaderComponentElement);
    });
  }

  private destroyLoader() {
    if (this.loaderComponentInstance) {
      this.loaderDestroy$.next();
      this.loaderComponentInstance.destroy();
    }
  }*/
}
